import { blobToFile } from "~/js/network/asset-storage";
import { config } from "~/js/network/network-handler";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { type DownloadLinksResponse, FileApi } from "../api/fileApi";
import { getPublicMessageFromError } from "./errorHelpers";
import { convertImageToPng } from "./imageHelpers";

export type IFileCustomFields = {
	objectUrl?: string;
	height?: number;
	width?: number;
	valid?: boolean;
	optionalName?: string;
	fileId?: number;
	isStoredRemote?: boolean;
	originalSrc?: string;
	duration?: number;
	videoHeight?: number;
	videoWidth?: number;
};

export type IFileType = File & IFileCustomFields;

export const getFileSrcToCdnOrRelative = (fileName: string) => {
	const prefix = `public/${fileName}`;
	if (config.FRONT_CDN_URL) {
		return config.FRONT_CDN_URL + prefix;
	}
	return `${window.location.origin}/${prefix}`;
};

export const helperCreateObjectUrl = (file: IFileType) => {
	if (file.objectUrl) {
		return file.objectUrl;
	}
	const url = URL.createObjectURL(file);
	file.objectUrl = url;
	return url;
};

export const helperRevokeObjectURL = (file: IFileType) => {
	if (file?.objectUrl) {
		URL.revokeObjectURL(file.objectUrl);
		file.objectUrl = undefined;
	}
};

export const downloadAsset = (file: IFileType) => {
	const a = document.createElement("a");
	document.body.append(a);
	a.setAttribute("blank", "_target");

	a.href = helperCreateObjectUrl(file);

	a.download = file.optionalName
		? `${file.optionalName}.${file.name.split(".").pop()}`
		: file.name;
	a.click();
	setTimeout(() => {
		a.remove();
	}, 100);
};

export const downloadAssetFromDataUrl = (url: string, name = "canvas.png") => {
	const a = document.createElement("a");
	document.body.append(a);
	a.href = url;
	a.download = name;
	a.click();
	setTimeout(() => {
		a.remove();
	}, 100);
};

/**
 * @description return map fileId to DownloadLinksResponse
 * @param links
 * @param customMap if custom map is pased will mutate it
 * @returns
 */
const mapIdListToLinkResponse = (links: DownloadLinksResponse[], customMap?: fileIdsMap) => {
	const map: fileIdsMap = customMap || {};
	for (const linkItem of links) {
		map[linkItem.file_id] = linkItem;
	}
	return map;
};

/**
 * @description get links list by file ids and return map from it Map<id, DownloadLinksResponse>
 * @param filesIdsList
 * @returns
 */
export const getLinksAndMap = async (filesIdsList: number[]) => {
	const map: fileIdsMap = {};
	if (filesIdsList.length === 0) return map;
	try {
		const linksList = await FileApi.getLinksToAwsListByFileId(filesIdsList);
		mapIdListToLinkResponse(linksList, map);
	} catch (e: any) {
		console.log(e);
		EventAnalytics.sendErrorInfo("get-files-ids-list-map", {
			message: e?.toString?.() || e?.message || "get-files-ids-list-map"
		});
	}

	return map;
};

export type fileIdsMap = Record<number, DownloadLinksResponse>;

/**
 * @description sync file with remote storage and extract file name or fileSrc based on type
 * @param file
 * @returns
 */
export const getFileData = async (
	file: File,
	abortSignal?: AbortSignal
): Promise<{ fileName: string; fileId: number } | { fileSrc: string; fileId: number }> => {
	const fileId = await FileApi.syncFileWithStorageAsync(file, { abortSignal });
	if (file.type === "application/pdf") {
		return { fileName: file.name, fileId };
	}
	const url = await FileApi.getLinkToAwsByFileId(fileId, abortSignal);

	return { fileSrc: url.download_link, fileId };
};
export type LocalData = { fileName: string } | { fileSrc: string };
/**
 * @description extract file name or file url from file
 * @param file
 * @returns
 */
export const getFileLocalData = (file: File): LocalData => {
	if (file.type === "application/pdf") {
		return { fileName: file.name };
	}

	return { fileSrc: helperCreateObjectUrl(file) };
};

/**
 * @description check if file is image
 * @param file
 * @returns
 */
export const isFileImage = (file: IFileType): boolean => {
	return file.type.split("/")[0] === "image";
};

export const handlePasteImageEvent = (event: ClipboardEvent) => {
	const items = event.clipboardData;
	if (!items?.files) {
		return null;
	}
	return Array.from(items.files);
};

export function createBlob(data: string, type: string): Blob {
	return new Blob([data], { type });
}

export async function setClipboard(text: string) {
	const type = "text/plain";
	const blob = new Blob([text], { type });
	const data = [new ClipboardItem({ [type]: blob })];
	await navigator.clipboard.write(data);
}

export const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		if (blob) {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
			reader.onabort = (error) => reject(error);
			reader.readAsDataURL(blob);
		}
	});
};

export const convertHeicToPng = async (file: IFileType) => {
	if (
		file.type === "image/heic" ||
		file.type === "image/heif" ||
		file.name.toLowerCase().endsWith(".heic") ||
		file.name.toLowerCase().endsWith(".heif")
	) {
		try {
			const heic2any = (await import("heic2any")).default;
			const convertedBlob = await heic2any({
				blob: file,
				toType: "image/png",
				quality: 0.8
			});
			const fileName = `${file.name.split(".")[0]}.png`;
			return blobToFile(convertedBlob, fileName, "image/png");
		} catch (error) {
			throw new Error(
				`Failed to convert "${file.name}": ${getPublicMessageFromError(error)}`
			);
		}
	}
	return file;
};

export const convertAvifToPng = async (file: IFileType) => {
	if (file.type === "image/avif" || file.name.toLowerCase().endsWith(".avif")) {
		try {
			const img = await convertImageToPng(file);
			if (!img) throw new Error(`Failed to convert "${file.name}"`);
			return img;
		} catch (error) {
			throw new Error(
				`Failed to convert "${file.name}": ${getPublicMessageFromError(error)}`
			);
		}
	}
	return file;
};

export const renameFile = (file: IFileType, newName: string) => {
	return new File([file], newName, { type: file.type });
};

export const lovercaseFileFormat = (file: IFileType) => {
	const fileType = file.name.split(".").pop();
	if (!fileType) return file;
	if (fileType === fileType.toLowerCase()) {
		return file;
	}
	return renameFile(
		file,
		`${file.name.split(".").slice(0, -1).join(".")}.${fileType.toLowerCase()}`
	);
};

export const isFileVideo = (file: IFileType): boolean => {
	return file.type.startsWith("video/");
};

export const isFileAudio = (file: IFileType): boolean => {
	return file.type.startsWith("audio/");
};
